/* eslint-disable no-console */
import { Socket } from 'socket.io-client';

import packageInfo from '../../package.json';

import { session, SESSION_KEYS } from 'utils/storage';
import { hasWorkOSOverride, hasWorkOSSession } from 'utils/workos';

export interface SocketResProps {
  message: string;
  status: boolean;
}

export function socketInit(socket: Socket, accessToken: string) {
  if (socket.connected) {
    return;
  }
  const token = sessionStorage.getItem(SESSION_KEYS.SOCKET_ACCESS_TOKEN) || accessToken;
  socket.auth = { authorization: token };
  socket.connect();
}
export function socketClose(socket: Socket) {
  socket.auth = { authorization: '' };
  socket.disconnect();
}

const WORKOS_RECONNECT_COOLDOWN_MS = 30_000;
const lastWorkOSReconnectAt = new WeakMap<Socket, number>();
let workOSSessionRefreshPromise: null | Promise<Response> = null;

const refreshWorkOSSession = (): Promise<Response> => {
  if (!workOSSessionRefreshPromise) {
    const staffId = session.get(SESSION_KEYS.STAFF_ID);
    workOSSessionRefreshPromise = fetch(import.meta.env.VITE_BASE_URL + '/auth/session', {
      credentials: 'include',
      headers: {
        'API-KEY': import.meta.env.VITE_API_KEY ?? '',
        'App-Version': packageInfo.version,
        Platform: 'web-patient',
        ...(staffId ? { 'impersonation-mode-by': staffId } : {}),
        ...(hasWorkOSOverride() ? { 'X-WorkOS-Override': 'true' } : {})
      },
      method: 'GET'
    }).finally(() => {
      workOSSessionRefreshPromise = null;
    });
  }
  return workOSSessionRefreshPromise;
};

/**
 * The socket server cannot refresh a WorkOS session itself (refresh tokens are single-use and it
 * cannot set the browser cookie), so it rejects an expired session with AUTH_EXPIRED. Refreshing
 * goes through GET /auth/session, which re-seals the cookie the next handshake sends. The cooldown
 * stops a connect_error -> refresh -> connect_error loop if the handshake keeps failing. All
 * sockets share the in-flight refresh because they use the same browser session cookie.
 */
async function reconnectWithWorkOSSession(socket: Socket) {
  const lastAttemptAt = lastWorkOSReconnectAt.get(socket) ?? 0;
  if (Date.now() - lastAttemptAt < WORKOS_RECONNECT_COOLDOWN_MS) {
    return console.warn('Socket reconnect skipped: WorkOS session was refreshed too recently');
  }
  lastWorkOSReconnectAt.set(socket, Date.now());

  const authorization = typeof socket.auth === 'object' ? (socket.auth.authorization ?? '') : '';
  try {
    socketClose(socket);
    const res = await refreshWorkOSSession();
    if (!res.ok) {
      return console.warn(
        `Socket reconnect skipped: WorkOS session refresh failed (${res.status})`
      );
    }
    socketInit(socket, authorization);
  } catch (e) {
    console.warn(e);
  }
}

export async function socketReconnect(socket: Socket, refreshToken: string) {
  if (hasWorkOSSession()) {
    return reconnectWithWorkOSSession(socket);
  }
  if (!refreshToken) {
    return console.warn('No refresh token available');
  }
  try {
    socketClose(socket);
    const headers: Record<string, string> = {
      'API-KEY': import.meta.env.VITE_API_KEY ?? '',
      'Content-Type': 'application/json'
    };
    if (hasWorkOSOverride()) {
      headers['X-WorkOS-Override'] = 'true';
    }
    const res = await fetch(import.meta.env.VITE_BASE_URL + '/auth/token-renewal', {
      body: JSON.stringify({ refreshToken }),
      credentials: 'include',
      headers,
      method: 'POST'
    });
    const data = await res.json();
    socketInit(socket, data.data?.accessToken || '');
  } catch (e) {
    console.warn(e);
  }
}

export const errorCallBack = (event: string, res: SocketResProps | Error) => {
  if ('status' in res && res.status) {
    console.log(`socket ${event} => ${res.message}`);
  } else {
    console.error(`socket ${event} error => ${res.message}`);
  }
};
